import React, { useState } from 'react';
import PremiumPageLayout from '../components/PremiumPageLayout';
import { ChevronDown, HelpCircle } from 'lucide-react';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: 'Programs',
      questions: [
        {
          q: 'What is the difference between the two Job Ready programs?',
          a: 'IT Job Ready System Engineer is designed for complete beginners with no IT experience. Cloud and DevOps Job Ready is for those with some IT background (6+ months) who want to advance into cloud and automation roles.'
        },
        {
          q: 'How long are the programs?',
          a: 'Both programs are 12 weeks long. IT Job Ready requires 10-15 hours/week, while Cloud and DevOps requires 15-20 hours/week due to more advanced content.'
        },
        {
          q: 'Are the classes live or recorded?',
          a: 'We offer a hybrid model: live mentor sessions twice a week, on-demand video content you can watch anytime, and hands-on lab environments available 24/7.'
        },
        {
          q: 'Do I get a certificate?',
          a: 'Yes! You receive a CareerLift completion certificate. Additionally, Cloud and DevOps students prepare for AWS, Azure, or GCP certification exams.'
        }
      ]
    },
    {
      category: 'Admission',
      questions: [
        {
          q: 'Do I need an IT degree or background?',
          a: 'No degree required! IT Job Ready System Engineer is specifically designed for complete beginners. Cloud and DevOps requires some basic IT knowledge (6+ months experience or completed IT foundations).'
        },
        {
          q: 'What are the technical requirements?',
          a: 'You need a computer (Windows, Mac, or Linux), stable internet connection (10 Mbps+), and willingness to learn. We provide access to all cloud labs and tools.'
        },
        {
          q: 'Is there an age limit?',
          a: 'No age limits! We have successful graduates from 18 to 55+. What matters is your commitment and willingness to learn.'
        }
      ]
    },
    {
      category: 'Costs & Payment',
      questions: [
        {
          q: 'How much does the program cost?',
          a: 'IT Job Ready System Engineer: $3,500 AUD. Cloud and DevOps Job Ready: $4,500 AUD. Payment plans available.'
        },
        {
          q: 'Do you offer payment plans?',
          a: 'Yes! We offer flexible payment plans: pay in full (5% discount), 3 monthly installments, or 6 monthly installments.'
        },
        {
          q: 'Is there a money-back guarantee?',
          a: 'We offer a 7-day satisfaction guarantee. If you are not satisfied within the first week, we will provide a full refund.'
        }
      ]
    },
    {
      category: 'Career Support',
      questions: [
        {
          q: 'Do you guarantee a job?',
          a: 'We cannot guarantee employment, but we provide comprehensive career support: resume building, interview preparation, portfolio development, and job application guidance.'
        },
        {
          q: 'What kind of jobs can I get after completing?',
          a: 'IT Job Ready graduates typically land: IT Support Specialist, Help Desk Technician, Junior System Administrator ($65K-$85K). Cloud and DevOps graduates: Cloud Support Engineer, Junior DevOps Engineer, Platform Engineer ($90K-$120K).'
        },
        {
          q: 'Do you help with resume and interviews?',
          a: 'Yes! We provide 1:1 resume review, mock interviews, LinkedIn profile optimization, and access to our job board with partner companies.'
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <PremiumPageLayout>
      <div className="relative py-24 px-4 overflow-hidden">
        <div className="max-w-[1000px] mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full border border-[#6366F1]/30 bg-[#6366F1]/5">
              <HelpCircle size={16} className="text-[#6366F1]" />
              <span className="text-sm font-semibold text-[#6366F1]">Frequently Asked Questions</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
              Got Questions? We've Got Answers
            </h1>
            <p className="text-xl text-[#C7D2FE] max-w-3xl mx-auto">
              Everything you need to know about our Job Ready programs, admissions, and career support.
            </p>
          </div>

          {/* FAQ Categories */}
          <div className="space-y-12">
            {faqs.map((category, catIdx) => (
              <div key={catIdx}>
                <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
                  {category.category}
                </h2>
                <div className="space-y-4">
                  {category.questions.map((faq, qIdx) => {
                    const key = `${catIdx}-${qIdx}`;
                    const isOpen = openIndex === key;
                    return (
                      <div
                        key={qIdx}
                        className="rounded-[20px] border border-white/10 backdrop-blur-sm overflow-hidden transition-all duration-300"
                        style={{ background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(99, 102, 241, 0.03) 100%)' }}
                      >
                        <button
                          onClick={() => toggleFAQ(catIdx, qIdx)}
                          className="w-full flex items-center justify-between p-6 text-left hover:bg-white/5 transition-colors"
                        >
                          <span className="text-lg font-semibold text-white pr-8">{faq.q}</span>
                          <ChevronDown
                            size={24}
                            className={`flex-shrink-0 text-[#6366F1] transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                          />
                        </button>
                        <div
                          className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}
                        >
                          <div className="px-6 pb-6 text-[#C7D2FE] leading-relaxed">
                            {faq.a}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Contact CTA */}
          <div 
            className="mt-16 p-8 rounded-[24px] border border-white/10 backdrop-blur-md text-center"
            style={{ 
              background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(37, 99, 235, 0.04) 100%)',
              boxShadow: '0 22px 65px rgba(15, 23, 42, 0.40), inset 0 1px 0 0 rgba(255,255,255,0.1)'
            }}
          >
            <h3 className="text-2xl font-bold text-white mb-4">Still have questions?</h3>
            <p className="text-[#C7D2FE] mb-6">
              Our team is here to help. Get in touch and we'll answer any questions you have.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-semibold transition-all duration-300 hover:shadow-[0_20px_50px_rgba(37,99,235,0.4)]"
            >
              Contact Us
            </a>
          </div>
        </div>

        {/* Decorative Glows */}
        <div className="absolute top-20 right-20 w-96 h-96 bg-[#6366F1]/10 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-[#2563EB]/10 rounded-full blur-[120px] pointer-events-none"></div>
      </div>
    </PremiumPageLayout>
  );
};

export default FAQPage;
